import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { gaEcommerce, gaItem } from "@/lib/track";

const ShopCartContext = createContext();
const KEY = "samtwo_shop_cart"; // distinct from the Licensing cart - never shared

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};

export const cartGaItems = (items) => items.map((i) => gaItem({
  id: i.kind === "digital" ? i.content_id : i.printful_variant_id || i.product_id,
  name: i.title, category: "shop", priceCents: i.unit_price_cents, quantity: i.qty || 1,
}));

const keyFor = (i) =>
  i.kind === "digital"
    ? `d:${i.content_id}`
    : `p:${i.product_id}:${i.printful_variant_id || `${i.color}/${i.size}`}`;

export function ShopCartProvider({ children }) {
  const [items, setItems] = useState(load);
  const [isOpen, setIsOpen] = useState(false);
  const [wantCheckout, setWantCheckout] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* noop */ }
  }, [items]);

  const addItem = (raw) => {
    const item = { ...raw, key: keyFor(raw) };
    gaEcommerce("add_to_cart", { items: cartGaItems([{ ...item, qty: item.qty || 1 }]), valueCents: (item.unit_price_cents || 0) * (item.qty || 1) });
    setItems((prev) => {
      const found = prev.find((p) => p.key === item.key);
      if (found) {
        if (item.kind === "physical") {
          return prev.map((p) => (p.key === item.key ? { ...p, qty: (p.qty || 1) + (item.qty || 1) } : p));
        }
        return prev; // digital: already in cart, no dupes
      }
      return [...prev, { ...item, qty: item.qty || 1 }];
    });
    setIsOpen(true);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((p) => p.key !== key));
  const clear = () => setItems([]);
  const openCart = () => setIsOpen(true);
  const closeCart = () => { setIsOpen(false); setWantCheckout(false); };
  const startCheckout = () => { setIsOpen(true); setWantCheckout(true); };
  const consumeWantCheckout = () => setWantCheckout(false);

  const count = useMemo(() => items.reduce((n, i) => n + (i.qty || 1), 0), [items]);
  const subtotalCents = useMemo(() => items.reduce((s, i) => s + (i.unit_price_cents || 0) * (i.qty || 1), 0), [items]);
  const hasPhysical = useMemo(() => items.some((i) => i.kind === "physical"), [items]);
  const isInCart = (k) => items.some((i) => i.key === k);

  return (
    <ShopCartContext.Provider value={{
      items, addItem, removeItem, clear, count, subtotalCents, hasPhysical, isInCart, keyFor,
      isOpen, openCart, closeCart, startCheckout, wantCheckout, consumeWantCheckout,
    }}>
      {children}
    </ShopCartContext.Provider>
  );
}

export const useShopCart = () => useContext(ShopCartContext);
export const SHOP_CART_RESUME = "samtwo_shop_cart_resume";
export { keyFor as shopKeyFor };
